import { Dayjs } from 'dayjs';
import { LegacyCustomerOrderBase } from '../../contracts/customer-order.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { PaymentType } from './payment-type.js';
import { PaymentMethod } from './payment-method.js';
import { OnlinePaymentDetails } from './online-payment-details.js';
import { ComponentList } from '../../components/core/component-list.js';
import { Language } from './language.js';
import { Price } from './price.js';
import { OpenAccessPass } from './open-access-pass/open-access-pass.js';
import { LegacyOrderHook } from './legacy-order-hook.js';
import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { Component } from '../../components/core/component.js';
import { TimeZone } from '../../time/timezone.js';
import { generateUniqueDatabaseId, isSameData, isSameList } from '../dto/data-model-utils.js';
import { PaymentStatus } from './payment-status.js';
import { CustomerAccountBase } from './customer-account-base.js';
import { CustomerProfile } from './customer-profile.js';
import { TrueTime } from '../../time/true-time.js';
import { InvoiceVat } from './invoicing/invoice-vat.js';
import { VersionRange } from '../core/version-range.js';
import { getCountryOfTimeZone, TimeZoneRegion } from './time-zone-region.js';
import { OrderSource } from './order-source.js';
import { OrderSideEffect } from './orders/order-side-effect.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { Country } from './country.js';
import { OrderDomain } from './order-domain.js';

export enum GymPassOrderStatus {
  Open = 'OPEN',
  Aborted = 'ABORTED',
  Canceled = 'CANCELED',
  Refunded = 'REFUNDED',
}

export class GymPassOrder extends DataModel implements LegacyCustomerOrderBase {
  static readonly TYPE_ID: string = '42f894d385874d4ea562b86fb70803a3';
  static readonly SCHEMA_VERSION: number = 6;

  public readonly isLegacyOrder = true;

  public readonly customerId: string | null;
  public readonly userId: string | null;
  public readonly language: Language | null;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly country: Country,
    public readonly timezone: TimeZoneRegion,
    public readonly status: GymPassOrderStatus,
    public readonly paymentType: PaymentType,
    public readonly paymentMethod: PaymentMethod,
    public readonly onlinePaymentDetails: OnlinePaymentDetails | null,
    public readonly source: OrderSource,
    public readonly domain: OrderDomain,
    public readonly components: ComponentList,
    public readonly isGuestOrder: boolean,
    public readonly cancelledDate: Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly applicationFee: Price | null,
    public readonly applicationFeeVat: InvoiceVat | null,
    public readonly price: Price,
    public readonly pass: OpenAccessPass,
    public readonly sideEffects: OrderSideEffect[],
  ) {
    super();
    this.userId = pass.customer?.userId ?? null;
    this.customerId = pass.customer?.uniqueId ?? null;
    this.language = pass.customer?.language ?? null;
  }

  get $typeId(): string {
    return GymPassOrder.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(4, GymPassOrder.SCHEMA_VERSION);
  }

  get isOpen(): boolean {
    return this.status === GymPassOrderStatus.Open;
  }

  get isCanceled(): boolean {
    return this.status === GymPassOrderStatus.Canceled;
  }

  static create(opts: {
    pass: OpenAccessPass,
    creatorId: string,
    paymentMethod: PaymentMethod,
    price: Price | null,
    country: Country,
    timezone: TimeZoneRegion,
  }): GymPassOrder {
    return new GymPassOrder(
        generateUniqueDatabaseId(),
        opts.creatorId,
        TrueTime.now(),
        opts.pass.businessRef.clone(),
        opts.country,
        opts.timezone,
        GymPassOrderStatus.Open,
        PaymentType.OnDelivery,
        opts.paymentMethod,
        null,
        OrderSource.Admin,
        OrderDomain.inTime,
        new ComponentList(),
        false,
        null,
        null,
        null,
        null,
        opts.price ?? opts.pass.price.clone(),
        opts.pass,
        [],
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GymPassOrder {
    return new GymPassOrder(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        toEnumFromString(MapUtils.get<string>(map, 'country'), Country),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        toEnumFromString(MapUtils.get<string>(map, '$status'), GymPassOrderStatus),
        toEnumFromString(MapUtils.get<string>(map, 'paymentType'), PaymentType),
        toEnumFromString(MapUtils.get<string>(map, 'paymentMethod'), PaymentMethod),
        context.fromDataOrNull<OnlinePaymentDetails>(MapUtils.getOrNull<SerializedData>(map, 'onlinePaymentDetails')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), OrderSource),
        toEnumFromString(MapUtils.get<string>(map, 'domain'), OrderDomain),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        ),
        MapUtils.get<boolean>(map, 'isGuestOrder'),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'cancelledDate')),
        MapUtils.getOrNull<string>(map, 'cancelledBy'),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'applicationFee')),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'applicationFeeVat'), InvoiceVat),
        Price.decode(MapUtils.get<string>(map, 'price')),
        context.fromData<OpenAccessPass>(MapUtils.get<SerializedData>(map, 'pass')),
        MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => context.fromData<OrderSideEffect>(value as SerializedData)),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      '$status': fromEnumAsString(this.status),
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'userId': this.userId,
      'customerId': this.customerId,
      'businessRef': this.businessRef.encode(),
      'country': fromEnumAsString(this.country),
      'timezone': fromEnumAsString(this.timezone),
      'creationDate': context.encodeDate(this.creationDate),
      'isGuestOrder': this.isGuestOrder,
      'components': this.components.serialize(context),
      'paymentType': fromEnumAsString(this.paymentType),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'onlinePaymentDetails': context.toDataOrNull(this.onlinePaymentDetails),
      'applicationFee': this.applicationFee?.encode() ?? null,
      'applicationFeeVat': fromEnumAsStringOrNull(this.applicationFeeVat),
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'passTemplateId': this.pass.templateId,
      'passId': this.pass.uniqueId,
      'pass': context.toData(this.pass),
      'price': this.price.encode(),
      'source': fromEnumAsString(this.source),
      'domain': fromEnumAsString(this.domain),
    };
  }

  clone(): this {
    return new GymPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.price.clone(),
        this.pass.clone(),
        this.sideEffects.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: GymPassOrder): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.onlinePaymentDetails, this.onlinePaymentDetails) &&
        isSameList(other.sideEffects, this.sideEffects, isSameData) &&
        other.components.isSame(this.components) &&
        other.country === this.country &&
        other.timezone === this.timezone &&
        other.source === this.source &&
        other.domain === this.domain &&
        other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.cancelledDate === this.cancelledDate &&
        other.cancelledBy === this.cancelledBy &&
        other.applicationFeeVat == this.applicationFeeVat &&
        isSameData(other.applicationFee, this.applicationFee) &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameData(other.price, this.price) &&
        isSameData(other.pass, this.pass) &&
        other.creationDate === this.creationDate &&
        other.status === this.status &&
        other.paymentMethod === this.paymentMethod &&
        other.paymentType === this.paymentType &&
        other.isGuestOrder === this.isGuestOrder;
  }

  copyWithPaymentDetails(paymentDetails: OnlinePaymentDetails): this {
    return new GymPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        paymentDetails,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.price.clone(),
        this.pass.clone(),
        this.sideEffects.map((e) => e.clone()),
    ) as this;
  }

  copyWithPaymentStatus(status: PaymentStatus): this {
    return new GymPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.copyWith({ status }) ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.price.clone(),
        this.pass.clone(),
        this.sideEffects.map((e) => e.clone()),
    ) as this;
  }

  copyWithPaymentMethod(paymentMethod: PaymentMethod): this {
    return new GymPassOrder(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.price.clone(),
        this.pass.clone(),
        this.sideEffects.map((e) => e.clone()),
    ) as this;
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return new GymPassOrder(
        this.uniqueId,
        account.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.country,
        this.timezone,
        this.status,
        this.paymentType,
        this.paymentMethod,
        this.onlinePaymentDetails?.clone() ?? null,
        this.source,
        this.domain,
        this.components.clone(),
        this.isGuestOrder,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
        this.applicationFee?.clone() ?? null,
        this.applicationFeeVat,
        this.price.clone(),
        this.pass.clone(),
        this.sideEffects.map((e) => e.clone()),
    ) as this;
  }

  tryGetCustomer(): CustomerProfile | null {
    return this.pass.customer;
  }

  hasComponentByTypeId(typeId: string): boolean {
    return this.components.hasComponentByTypeId(typeId);
  }

  mustFindComponentByTypeId<C extends Component>(typeId: string): C {
    return this.components.mustFindComponentByTypeId<C>(typeId);
  }

  findComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.findComponentByTypeId<C>(typeId);
  }

  findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
    return this.components.findComponentByInstanceId<C>(instanceId);
  }

  findComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.findComponentsByTypeId<C>(typeId);
  }

  addComponent(component: Component): void {
    this.components.addComponent(component);
  }

  addComponents(list: Component[]): void {
    this.components.addComponents(list);
  }

  removeComponentByTypeId(typeId: string): boolean {
    return this.components.removeComponentByTypeId(typeId);
  }

  removeComponentByInstanceId(instanceId: string): boolean {
    return this.components.removeComponentByInstanceId(instanceId);
  }

  removeComponentAt(index: number): void {
    this.components.removeComponentAt(index);
  }

  removeAllComponents(): void {
    this.components.removeAllComponents();
  }

  popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.popAllComponentsByTypeId(typeId);
  }

  popComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.popComponentByTypeId(typeId);
  }

  popComponent<C extends Component>(component: C): C | null {
    return this.components.popComponent(component);
  }

  replaceComponent<C extends Component>(replacement: C): boolean {
    return this.components.replaceComponent(replacement);
  }

  addOrReplaceComponent<C extends Component>(component: C): void {
    this.components.addOrReplaceComponent(component);
  }

  mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
    return this.components.mustFindComponentField<C, F>(componentTypeId, find);
  }

  findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
    return this.components.findComponentField<C, F>(componentTypeId, find);
  }

  static encodedStatusSafe(status: GymPassOrderStatus): string {
    return fromEnumAsString(status);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof GymPassOrder;
  }

  static assertType(order: DataModel): order is GymPassOrder {
    return order instanceof GymPassOrder;
  }
}

export class GymPassOrderBuilder extends DataModelBuilder<GymPassOrder> {
  build(context: SerializationContext, map: SerializedData): GymPassOrder {
    return GymPassOrder.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: GymPassOrder): SerializedData | null {
    let paymentStatus: PaymentStatus | null = null;

    if(model.paymentType === PaymentType.Online) {
      paymentStatus = model.onlinePaymentDetails?.status ?? null;
    }
    else {
      paymentStatus = PaymentStatus.Succeeded;
    }

    return {
      'paymentStatus': fromEnumAsStringOrNull(paymentStatus),
      'paymentType': fromEnumAsString(model.paymentType),
      'paymentMethod': fromEnumAsString(model.paymentMethod),
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', GymPassOrderStatus, GymPassOrderStatus.Open);
    this.migrateEnum(map, 'paymentType', PaymentType, PaymentType.OnDelivery);
    this.migrateEnum(map, 'paymentMethod', PaymentMethod, PaymentMethod.Cash);

    // Needed for deserializing invoices with a graceful creation date! See builder of [OnlinePaymentDetails].
    const creationDate = context.decodeDate(MapUtils.getInteger(map, 'creationDate'));
    context.storage.set('orderCreationDate', creationDate);

    if(versionRange.contains(GymPassOrder.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GymPassOrder.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version parameter `applicationFeeVat` of type `InvoiceVat | null` was introduced.
    if(version < 2) {
      version = 2;
    }

    // In this version parameter `timezone` of type `TimeZoneRegion` was introduced.
    if(version < 3) {
      // Initial gyms were only in Romania.
      map['timezone'] = fromEnumAsString(TimeZoneRegion.EuropeBucharest);
      version = 3;
    }

    if(version < 4) {
      const onCompleteHooks = MapUtils.getList<LegacyOrderHook>(map, 'onCompleteHooks', (value) => {
        return context.fromData<LegacyOrderHook>(value as SerializedData);
      });

      const sideEffects = onCompleteHooks.map((e) => e.toSideEffect()).filter(isNotNullOrUndefined);

      map['sideEffects'] = sideEffects.map((e) => context.toData(e));
      version = 4;
    }

    // In this version parameter `country` of type `Country` was introduced.
    if(version < 5) {
      // Initial gyms were only in Romania.
      const timezone = toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion);
      map['country'] = fromEnumAsString(getCountryOfTimeZone(timezone) ?? Country.Romania);
      version = 5;
    }

    // In this version parameter `domain` of type `OrderDomain` was introduced.
    if(version < 6) {
      map['domain'] = fromEnumAsString(OrderDomain.inTime);
      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
